import { DocumentType, PurchaseOrderTransition, getOrderTypeTranslationId } from '@synergy/models';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { PaloColumn, PaloFilterChips, PaloListView, PaloRowAction, PaloStatusPill } from '@synergy/components-palo';
import { PoolDocumentTitle } from '@synergy/components-next';
import { ReactElement, useEffect, useState } from 'react';
import { getPurchaseOrderStatusTranslationId } from '@synergy/utils';
import { useNavigate } from 'react-router';

import { ContactPresenter } from '../../../../common/components/contact-presenter/contact-presenter';
import { ConverterReceiptModal } from '../../../convert-receipt/converter-receipt-modal';
import { PaloContactName } from '../../../../common/components/palo/palo-contact-card';
import { PaloDocumentNumberCell } from '../../../../common/components/palo/palo-document-number-cell';
import { PurchaseOrder } from '../../../purchase-orders/models/purchaseOrder';
import { Transition } from '../../../convert-receipt/models/type-transition';
import {
    deletePurchaseOrder,
    duplicatePurchaseOrder,
    fetchPurchaseOrder,
    requestEnd as purchaseOrderRequestEnd,
    resetPurchaseOrderState,
} from '../../../purchase-orders/purchase-order-actions';
import { fetchTransitions, resetTransitions } from '../../../../common/actions/transitions-activity-actions';
import { lockedPerson } from '../../../../common/utils/locked-partner-notice';
import { requestingSelector } from '../../store/dashboard-selectors';
import { transitionPurchaseOrderIntoInvoice } from '../../../convert-receipt/transition-actions';
import { useAppSelector } from '../../../../store/use-appstate-selector';
import { useDashboardSort } from '../../palo/use-dashboard-sort';
import { useStatusNames } from '../../../../common/components/filter/document-status-filter';
import { useThunkDispatch } from '../../../../store/use-thunk-dispatch';

const columns: PaloColumn<PurchaseOrder>[] = [
    {
        key: 'number',
        type: 'documentNumber',
        sortKey: 'number',
        head: <FormattedMessage id="app.table.column.number" />,
        cell: order => <PaloDocumentNumberCell number={order.number} alert={order.receiptAlert} />,
    },
    {
        key: 'status',
        type: 'status',
        sortKey: 'status',
        head: <FormattedMessage id="app.table.column.status" />,
        cell: order => (
            <PaloStatusPill
                status={order.status}
                label={<FormattedMessage id={getPurchaseOrderStatusTranslationId(order.status)} />}
            />
        ),
    },
    {
        key: 'type',
        type: 'classification',
        sortKey: 'type',
        head: <FormattedMessage id="app.table.column.type" />,
        cell: order => <FormattedMessage id={getOrderTypeTranslationId(order.type)} />,
    },
    {
        key: 'customer',
        type: 'name',
        sortKey: 'customerAccount',
        head: <FormattedMessage id="app.table.column.customer" />,
        cell: order => <PaloContactName company={order.customerAccount} kind="customer" />,
    },
    {
        key: 'supplier',
        type: 'name',
        sortKey: 'supplierAccount',
        head: <FormattedMessage id="app.table.column.supplier" />,
        cell: order => <PaloContactName company={order.supplierAccount} kind="supplier" />,
    },
    {
        key: 'responsibleContact',
        type: 'name',
        sortKey: 'responsibleContact',
        head: <FormattedMessage id="app.common.responsibleContact" />,
        cell: order => (
            <ContactPresenter
                person={order.responsibleContact}
                fullName={order.responsibleContact?.fullName}
                isDeleted={order.responsibleContact?.isDeleted}
                isLocked={order.responsibleContact?.isLocked}
            />
        ),
    },
    {
        key: 'createdAt',
        type: 'date',
        sortKey: 'createdAt',
        head: <FormattedMessage id="app.table.column.createdAt" />,
        cell: order => <FormattedDate value={order.createdAt} year="numeric" month="short" day="2-digit" />,
    },
];

export const DashboardOverviewPurchaseOrders = (): ReactElement => {
    const statusNames = useStatusNames('purchase-orders');
    const navigate = useNavigate();
    const { sort, onSortChange } = useDashboardSort('purchase-orders');
    const dispatch = useThunkDispatch();
    const [isConverterVisible, setIsConverterVisible] = useState(false);
    const purchaseOrder = useAppSelector(state => state.purchaseOrders.entity);
    const purchaseOrders = useAppSelector(state => state.purchaseOrders.flat);
    const requesting = useAppSelector(requestingSelector);

    const cleanupState = (): void => {
        dispatch(resetPurchaseOrderState());
        dispatch(purchaseOrderRequestEnd());
    };

    useEffect(() => {
        return cleanupState;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleConvertOpen = (id: number): void => {
        dispatch(resetPurchaseOrderState());
        dispatch(resetTransitions());
        dispatch(fetchTransitions(DocumentType.PurchaseOrder, id));
        dispatch(fetchPurchaseOrder(id));
        setIsConverterVisible(true);
    };

    const handleTransition = (transitionObject: Transition) =>
        dispatch(transitionPurchaseOrderIntoInvoice(transitionObject as PurchaseOrderTransition));

    const handleConvertClose = (): void => {
        cleanupState();
        setIsConverterVisible(false);
    };

    const rowActions = (order: PurchaseOrder): PaloRowAction[] => [
        { label: 'Öffnen', onClick: () => navigate('/purchase-orders/' + order.id) },
        { label: 'Umwandeln', onClick: () => handleConvertOpen(order.id) },
        {
            label: 'Duplizieren',
            onClick: () =>
                dispatch(
                    duplicatePurchaseOrder(order.id, [
                        order.customerAccount,
                        order.supplierAccount,
                        lockedPerson(order.customerContact),
                        lockedPerson(order.supplierContact),
                    ])
                ),
        },
        { label: 'Löschen', onClick: () => dispatch(deletePurchaseOrder(order.id)) },
    ];

    return (
        <>
            <PoolDocumentTitle titleTranslationId="app.navigation.dashboard.purchaseOrders" />
            <PaloFilterChips names={{ status: statusNames }} />
            <PaloListView<PurchaseOrder>
                embedded
                columns={columns}
                sort={sort}
                onSortChange={onSortChange}
                items={purchaseOrders.items}
                getRowId={order => order.id}
                requesting={requesting}
                emptyLabel="Keine Bestellungen."
                rowHref={order => '/purchase-orders/' + order.id}
                rowActions={rowActions}
            />

            {purchaseOrder && (
                <ConverterReceiptModal
                    isVisible={isConverterVisible}
                    source={purchaseOrder}
                    onClose={handleConvertClose}
                    onConvert={handleTransition}
                />
            )}
        </>
    );
};
